import { useState } from "react";
import { AtSign, Ban, Crown, KeyRound, Shield, ShieldAlert, ShieldOff, Trash2, Undo2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { tierDisplayName, type Tier } from "@/lib/tier";
import { BillingCard } from "@/components/admin/billing/BillingCard";
import { DeleteUserDialog } from "@/components/admin/DeleteUserDialog";
import { AdminRenameUsernameDialog } from "@/components/admin/AdminRenameUsernameDialog";
import { labelFor, TIER_OPTIONS, type AdminUser } from "./types";

type Props = {
  selected: AdminUser | null;
  onOpenChange: (open: boolean) => void;
  callerId: string | null;
  isSuperAdmin: boolean;
  actionBusy: boolean;
  callAction: (
    action: "set_user_tier" | "send_password_reset" | "ban_user" | "unban_user",
    extra?: Record<string, unknown>,
  ) => Promise<void>;
  refreshSelected: () => Promise<void>;
  onDeleted: () => Promise<void>;
};

export const UserDetailDrawer = ({
  selected, onOpenChange, callerId, isSuperAdmin, actionBusy,
  callAction, refreshSelected, onDeleted,
}: Props) => {
  const [confirmBan, setConfirmBan] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);

  return (
    <>
      <Sheet open={!!selected} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>{labelFor(selected)}</SheetTitle>
                <SheetDescription>{selected.email}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <section className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {selected.account_tier && (
                      <Badge variant="secondary">{tierDisplayName(selected.account_tier as Tier)}</Badge>
                    )}
                    {selected.roles.includes("super_admin") && (
                      <Badge className="gap-1"><Crown className="h-3 w-3" /> Super admin</Badge>
                    )}
                    {selected.roles.includes("admin") && !selected.roles.includes("super_admin") && (
                      <Badge variant="secondary" className="gap-1"><Shield className="h-3 w-3" /> Admin</Badge>
                    )}
                    {selected.is_banned && (
                      <Badge variant="destructive" className="gap-1"><Ban className="h-3 w-3" /> Banned</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-y-1 text-xs text-muted-foreground">
                    <span>Joined</span>
                    <span className="text-right text-foreground">{new Date(selected.created_at).toLocaleDateString()}</span>
                    <span>Last sign-in</span>
                    <span className="text-right text-foreground">
                      {selected.last_sign_in_at ? new Date(selected.last_sign_in_at).toLocaleDateString() : "—"}
                    </span>
                    {selected.is_banned && selected.banned_until && (
                      <>
                        <span>Banned until</span>
                        <span className="text-right text-foreground">{new Date(selected.banned_until).toLocaleString()}</span>
                      </>
                    )}
                  </div>
                </section>

                <BillingCard userId={selected.id} onTierChanged={refreshSelected} />

                {!isSuperAdmin && (
                  <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                    Only super admins can change a user's tier, send a password reset, or ban / unban an account.
                  </p>
                )}

                {isSuperAdmin && (
                  <>
                    <section className="space-y-2">
                      <div className="text-sm font-medium">Account tier</div>
                      <Select
                        value={selected.account_tier ?? "starter"}
                        onValueChange={(v) => callAction("set_user_tier", { tier: v })}
                        disabled={actionBusy}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIER_OPTIONS.filter((o) => o.value !== "any").map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </section>

                    <section className="space-y-2">
                      <div className="text-sm font-medium">Account actions</div>
                      <div className="flex flex-col gap-2">
                        <Button variant="outline" disabled={actionBusy} onClick={() => setRenameOpen(true)}>
                          <AtSign className="h-4 w-4" /> Rename username (override)
                        </Button>
                        <Button
                          variant="outline"
                          disabled={actionBusy || !selected.email}
                          onClick={() => callAction("send_password_reset")}
                        >
                          <KeyRound className="h-4 w-4" /> Send password reset email
                        </Button>

                        {!selected.is_banned && (
                          <Button
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            disabled={
                              actionBusy ||
                              selected.id === callerId ||
                              selected.roles.includes("admin") ||
                              selected.roles.includes("super_admin")
                            }
                            onClick={() => setConfirmBan(true)}
                          >
                            <Ban className="h-4 w-4" /> Ban account
                          </Button>
                        )}
                        {selected.is_banned && (
                          <Button variant="outline" disabled={actionBusy} onClick={() => callAction("unban_user")}>
                            <Undo2 className="h-4 w-4" /> Unban account
                          </Button>
                        )}

                        {(selected.roles.includes("admin") || selected.roles.includes("super_admin")) && !selected.is_banned && (
                          <p className="flex items-start gap-2 text-xs text-muted-foreground">
                            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            This user is an admin. Revoke their admin role from the Admins tab before banning.
                          </p>
                        )}
                      </div>
                    </section>

                    {selected.id !== callerId &&
                      !selected.roles.includes("admin") &&
                      !selected.roles.includes("super_admin") && (
                      <section className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                        <div className="text-sm font-medium text-destructive">Danger zone</div>
                        <p className="text-xs text-muted-foreground">
                          Permanent account closure. Cancels active subscriptions immediately,
                          deletes every Persona, workspace, and all owned content. There is no undo.
                        </p>
                        <Button variant="destructive" disabled={actionBusy} onClick={() => setConfirmDelete(true)}>
                          <Trash2 className="h-4 w-4" /> Permanently delete account
                        </Button>
                      </section>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmBan} onOpenChange={setConfirmBan}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ban this account?</AlertDialogTitle>
            <AlertDialogDescription>
              {selected ? labelFor(selected) : "This user"} will be unable to sign in until you unban them.
              Their data is preserved and they can be unbanned at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                setConfirmBan(false);
                await callAction("ban_user");
              }}
            >
              <ShieldOff className="h-4 w-4" /> Ban
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selected && (
        <DeleteUserDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          targetUserId={selected.id}
          targetEmail={selected.email}
          targetLabel={labelFor(selected)}
          onDeleted={onDeleted}
        />
      )}

      {selected && (
        <AdminRenameUsernameDialog
          open={renameOpen}
          onOpenChange={setRenameOpen}
          targetUserId={selected.id}
          targetLabel={labelFor(selected)}
          targetTier={selected.account_tier}
          onRenamed={refreshSelected}
        />
      )}
    </>
  );
};
